/** The venue's wall-clock timezone. Every booking date/time a guest enters or
 *  reads is Moscow time — the table is in Moscow, not on the guest's device.
 *  Without pinning this, a guest booking from UTC+7 who types 18:00 gets an
 *  instant that the bot and the admin panel both render as 14:00. */
export const VENUE_TZ = 'Europe/Moscow';
/** Moscow has been a fixed UTC+3 since 2014 (no DST), so a constant offset is
 *  enough to turn wall-clock parts into an absolute instant. */
export const VENUE_UTC_OFFSET = '+03:00';
function venueParts(input, opts) {
    const d = input instanceof Date ? input : new Date(input);
    // hourCycle h23 (not hour12:false) so midnight renders as 00, never 24.
    const fmt = new Intl.DateTimeFormat('ru-RU', {
        timeZone: VENUE_TZ,
        hourCycle: 'h23',
        ...opts,
    });
    const out = {};
    for (const p of fmt.formatToParts(d))
        out[p.type] = p.value;
    return out;
}
/** Combine a venue-local date (`YYYY-MM-DD`) and time (`HH:mm`) into an
 *  absolute ISO instant, independent of the caller's device timezone. */
export function venueIso(date, time) {
    const hhmm = time.length === 5 ? time : time.padStart(5, '0');
    return `${date}T${hhmm}:00${VENUE_UTC_OFFSET}`;
}
/** `YYYY-MM-DD` of an instant in venue time (defaults to now). */
export function venueDate(input = Date.now()) {
    const p = venueParts(input, { year: 'numeric', month: '2-digit', day: '2-digit' });
    return `${p.year}-${p.month}-${p.day}`;
}
/** `HH:mm` of an instant in venue time (defaults to now). */
export function venueTime(input = Date.now()) {
    const p = venueParts(input, { hour: '2-digit', minute: '2-digit' });
    return `${p.hour}:${p.minute}`;
}
/** `DD.MM.YYYY HH:mm` in venue time — the one booking datetime format shared
 *  by the bot, the admin panel and the mini app. */
export function formatVenueDateTime(input) {
    const p = venueParts(input, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
    return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
}
/** `DD.MM.YYYY` in venue time. */
export function formatVenueDate(input) {
    const p = venueParts(input, { year: 'numeric', month: '2-digit', day: '2-digit' });
    return `${p.day}.${p.month}.${p.year}`;
}
/** True when the running device is not already on venue time — used to decide
 *  whether the UI needs to spell out that entered times are Moscow times. */
export function deviceIsOffVenueTime(input = Date.now()) {
    const d = input instanceof Date ? input : new Date(input);
    const local = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return local !== venueTime(d);
}
